import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, UserPlus, UserCog } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin" },
  { value: "manager", label: "Manager" },
  { value: "office", label: "Office" },
  { value: "engineer", label: "Engineer" },
];

export default function UserForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: "",
    password: "",
    full_name: "",
    email: "",
    role: "engineer",
    active: true,
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/users/${id}`);
        setForm({
          username: data.username,
          password: "",
          full_name: data.full_name || "",
          email: data.email || "",
          role: data.role,
          active: data.active,
        });
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/users", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/users/${id}`, {
          full_name: form.full_name,
          email: form.email || null,
          role: form.role,
          active: form.active,
        });
        toast.success("User updated");
      } else {
        await api.post("/users", {
          username: form.username.trim(),
          password: form.password,
          full_name: form.full_name.trim(),
          email: form.email || null,
          role: form.role,
          active: form.active,
        });
        toast.success("User created");
      }
      navigate("/users");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="user-form-loading">Loading…</div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="user-form-page">
      <PageHeader
        testId="user-form-header"
        title={isEdit ? "Edit user" : "New user"}
        subtitle={isEdit ? "Update profile, role or activation state." : "Provision a new account for the field service team."}
        actions={
          <Button asChild variant="outline" size="sm" data-testid="user-form-back">
            <Link to="/users"><ArrowLeft className="h-4 w-4" /> Back</Link>
          </Button>
        }
      />

      <form onSubmit={submit} className="space-y-6 max-w-3xl">
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              {isEdit ? <UserCog className="h-4 w-4 text-primary" /> : <UserPlus className="h-4 w-4 text-primary" />}
              Account details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="user-form-username"
                value={form.username}
                onChange={(e) => update({ username: e.target.value })}
                required
                disabled={isEdit}
                placeholder="e.g. eng4"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                data-testid="user-form-full-name"
                value={form.full_name}
                onChange={(e) => update({ full_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="user-form-email"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
                placeholder="optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="role">Role</Label>
              <Select value={form.role} onValueChange={(v) => update({ role: v })}>
                <SelectTrigger id="role" data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isEdit && (
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="user-form-password"
                  value={form.password}
                  onChange={(e) => update({ password: e.target.value })}
                  required
                  minLength={6}
                  placeholder="At least 6 characters"
                />
              </div>
            )}
            <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-border bg-card p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">
                  Inactive users cannot log in.
                </div>
              </div>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => update({ active: v })}
                data-testid="user-form-active"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/users")} data-testid="user-form-cancel">
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="user-form-submit">
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create user"}
          </Button>
        </div>
      </form>
    </div>
  );
}
